import "ol/ol.css";
import Map from "ol/Map";
import { Draw } from "ol/interaction";
import MapBrowserEvent from "ol/MapBrowserEvent";
import { Polygon } from "ol/geom";
import { DrawEvent } from "ol/interaction/Draw";
import Feature from "ol/Feature";
import VectorSource from "ol/source/Vector";
import * as turf from "@turf/turf";
import { getArea } from "ol/sphere";
import { Coordinate } from "ol/coordinate";
import { ICommonView } from "@/components/pages/common/ICommonView";
import { MessageDefine } from "@/components/pages/common/MessageDefine";
/**
 * マウスダウンイベント定義値
 */
const EVNET_MOUSE_TYPE_DOWN = "pointerdown";

/**
 * マウス左クリックボタン定義値
 */
const EVENT_MOUSE_LEFT_BUTTON = 0;

/**
 * 描画開始イベント定義値
 */
const EVENT_DRAWSART = "drawstart";

/**
 * 描画終了イベント定義値
 */
const EVENT_DRAWEND = "drawend";

/**
 * ダブルクリック判定用時間(ミリ秒)定義値
 */
const DUBLE_CLICK_INTERVAL = 500;

/**
 * 面積計測処理
 */
export class AreaCalcInteraction {
  /**
   * 地図クラス
   */
  private _map: Map;

  /**
   * 図形描画用Intraction
   */
  private _drawInteraction: Draw;

  /**
   * 描画中図形
   */
  private _drawingFeature: Feature;

  /**
   * 画面共通処理用インタフェース
   */
  private _commonView: ICommonView;

  /**
   * 計測データソース
   */
  private _calSource: VectorSource;

  /**
   * ダブルクリック判定用前回時間
   */
  private _clcicTime: number;

  /**
   * 面積計測確定コールバック関数
   */
  private _areaCalcFixFunc: (area: number) => any;

  /**
   * コンストラクタ
   * @param map マップクラス
   * @param targetSource 計測データソース
   * @param messageLogic メッセージ通知用インタフェース
   */
  public constructor(
    map: Map,
    targetSource: VectorSource,
    commonView: ICommonView
  ) {
    this._map = map;
    this._calSource = targetSource;
    this._commonView = commonView;
  }

  /**
   * 面積計測モードを開始する。
   * @param areaCalcFixFunc 面積計測確定コールバック関数
   */
  public startCalc(areaCalcFixFunc: (areaDistanceValue: number) => any) {
    this._areaCalcFixFunc = areaCalcFixFunc;
    this._drawInteraction = new Draw({
      source: this._calSource,
      type: "Polygon",
      condition: this.drawCondition.bind(this),
      finishCondition: this.drawFinishCondition.bind(this),
    });

    this._map.addInteraction(this._drawInteraction);
    this._drawInteraction.on(EVENT_DRAWSART, this.drawStartEvent.bind(this));
    this._drawInteraction.on(EVENT_DRAWEND, this.drawEndEvent.bind(this));
  }

  /**
   * 面積計測モードを終了する。
   */
  public endCalc() {
    this._drawingFeature = null;
    this._drawInteraction.un(EVENT_DRAWSART, this.drawStartEvent.bind(this));
    this._drawInteraction.un(EVENT_DRAWEND, this.drawEndEvent.bind(this));
    this._map.removeInteraction(this._drawInteraction);
    this._calSource.clear();
  }

  /**
   * 図形描画開始イベント
   * 前回描画図形を削除し、描きはじめの図形を変数に格納する。
   * @param evt 描画開始イベント情報
   */
  private drawStartEvent(evt: DrawEvent) {
    this._calSource.clear();
    this._drawingFeature = evt.feature;
  }

  /**
   * 面積計測点追加完了イベント
   * 面積を計算し、面積計測結果通知用コールバック関数に通知する。
   * @param evt 描画終了イベント情報
   */
  private drawEndEvent(evt: DrawEvent) {
    const area = evt.feature.getGeometry();
    const areaDistance = getArea(area);
    this._areaCalcFixFunc(areaDistance);
  }

  /**
   * 面積計測点を削除する。
   */
  public clearCalcArea() {
    this._drawingFeature = null;
    this._drawInteraction.abortDrawing();
    this._calSource.clear();
  }

  /**
   * 計測点終了操作条件
   * 座標点が計算できる3点となっていないかチェックし、3点未満の場合は無効とする。
   * @returns true:有効、false無効
   */
  private drawFinishCondition(): boolean {
    const polyGoem: Polygon = <Polygon>this._drawingFeature.getGeometry();
    const polyCoords = polyGoem.getCoordinates();
    try {
      const kinkedPoly = turf.polygon(polyCoords);
      const clearnPoly = turf.cleanCoords(kinkedPoly);
      turf.unkinkPolygon(clearnPoly);
    } catch (e) {
      return false;
    }
    return true;
  }

  /**
   * 面積計測点追加イベント処理
   * @param evt イベント情報
   */
  private drawCondition(evt: MapBrowserEvent<any>): boolean {
    if (
      evt.type == EVNET_MOUSE_TYPE_DOWN &&
      evt.originalEvent.button == EVENT_MOUSE_LEFT_BUTTON
    ) {
      this._areaCalcFixFunc(0);
      //図形描画済の場合
      if (this._drawingFeature) {
        const polyGoem: Polygon = <Polygon>this._drawingFeature.getGeometry();
        const polyCoords = polyGoem.getCoordinates();
        const jdCoords = new Array<Coordinate>();
        let beforeCoords = polyCoords[0][0];
        //ダブルクリックの場合に同一点がpolyGoemに入るため、除外処理
        for (const coord of polyCoords[0]) {
          if (!(coord[0] == beforeCoords[0] && coord[1] == beforeCoords[1])) {
            jdCoords.push(coord);
            beforeCoords = coord;
          }
        }

        //重複を除いた座標点が3点以上の場合、交差判定を行う。
        if (jdCoords.length > 2) {
          try {
            const kinkedPoly = turf.polygon(polyCoords);
            const clearnPoly = turf.cleanCoords(kinkedPoly);
            const unkinkedPoly = turf.unkinkPolygon(clearnPoly);
            if (unkinkedPoly.features.length > 1) {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.W0012.getMessage()
              );
              return false;
            }
          } catch (e) {
            return false;
          }
        } else {
          //2点以下の場合
          const nowTime = performance.now();
          //ダブルクリック時判定
          if (nowTime - this._clcicTime <= DUBLE_CLICK_INTERVAL) {
            this._clcicTime = nowTime;
            const coords = polyGoem.getCoordinates();
            const undoCoorrds: Array<Coordinate> = new Array<Coordinate>();

            for (let i = 0; i < coords[0].length; i++) {
              if (i != coords[0].length - 2) {
                undoCoorrds.push(coords[0][i]);
              }
            }
            this._commonView._messageLogic.onOpenMessage(
              MessageDefine.W0013.getMessage()
            );
          }
          this._clcicTime = nowTime;
        }
      }
      return true;
      //マウスのクリックは左ボタン以外を禁止
    } else if (evt.type == EVNET_MOUSE_TYPE_DOWN) {
      return false;
    }
    return true;
  }
}
